import { useState } from "react";
import { Bookmark, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { saveToolResume } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * "Save my answers" magic-link form. Lets unauthenticated tool users persist
 * their in-progress (or just-completed) answers and resume from any device.
 * Distinct from EmailResultsForm: this is form-state-preserving, not just
 * a results email. Both can coexist on the same result page.
 */
export default function SaveMyAnswersForm({
    toolSlug,
    inputs,
    result,
    zipCode,
}) {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting || !email.trim()) return;
        setSubmitting(true);
        try {
            await saveToolResume({
                tool_name: toolSlug,
                email: email.trim().toLowerCase(),
                inputs: inputs || {},
                result: result || {},
                zip_code: zipCode || null,
            });
            track("tool_save_resume_submitted", { tool_name: toolSlug });
            setSent(true);
            toast.success("Resume link sent. Check your inbox.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Could not save. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div
                className="bl-card p-6 border border-bl"
                data-testid="save-resume-success"
            >
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[var(--bl-moss)] mt-0.5" />
                    <div>
                        <h4 className="font-display font-semibold text-base text-bl">
                            Saved. Check your email for the link.
                        </h4>
                        <p className="mt-1 text-sm text-bl-muted">
                            Valid for 14 days. Open the link on any device to
                            restore your answers.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            className="bl-card p-6 border border-bl"
            data-testid="save-resume-form"
        >
            <div className="flex items-start gap-3 mb-4">
                <Bookmark className="h-5 w-5 text-bl-primary mt-0.5" />
                <div>
                    <h4 className="font-display font-semibold text-base text-bl">
                        Save my answers
                    </h4>
                    <p className="mt-1 text-xs text-bl-muted">
                        Email yourself a 14-day resume link so you can pick up
                        on any device.
                    </p>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
                <input
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bl-input flex-1"
                    data-testid="save-resume-email"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    className="bl-btn-secondary justify-center gap-2"
                    data-testid="save-resume-submit"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending…
                        </>
                    ) : (
                        "Send resume link"
                    )}
                </button>
            </div>
        </form>
    );
}
